export interface StoryOverviewPosition {
  x: number
  y: number
}

export const STORY_OVERVIEW_DENSE_THRESHOLD = 30

export function storyOverviewLayoutTokens(nodeCount: number) {
  return nodeCount >= STORY_OVERVIEW_DENSE_THRESHOLD
    ? { width: 920, height: 620, minimumDistance: 116, nodeSize: 58 }
    : { width: 680, height: 430, minimumDistance: 104, nodeSize: 66 }
}

export function minimumPresetDistance(positions: Readonly<Record<string, StoryOverviewPosition>>) {
  const points = Object.values(positions)
  let minimumDistance = Number.POSITIVE_INFINITY
  for (let leftIndex = 0; leftIndex < points.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < points.length; rightIndex += 1) {
      minimumDistance = Math.min(minimumDistance, Math.hypot(
        points[leftIndex].x - points[rightIndex].x,
        points[leftIndex].y - points[rightIndex].y,
      ))
    }
  }
  return minimumDistance
}

export function normalizePresetPositions(
  positions: Readonly<Record<string, StoryOverviewPosition>>,
  width = storyOverviewLayoutTokens(Object.keys(positions).length).width,
  height = storyOverviewLayoutTokens(Object.keys(positions).length).height,
) {
  const entries = Object.entries(positions)
  if (!entries.length) return {}
  const xs = entries.map(([, point]) => point.x)
  const ys = entries.map(([, point]) => point.y)
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  const spanX = Math.max(maxX - minX, 1)
  const spanY = Math.max(maxY - minY, 1)
  const scale = Math.min(width / spanX, height / spanY)
  const centreX = (minX + maxX) / 2
  const centreY = (minY + maxY) / 2
  const normalized = Object.fromEntries(entries.map(([id, point]) => [id, {
    x: 410 + (point.x - centreX) * scale,
    y: 310 + (point.y - centreY) * scale,
  }]))
  return relaxPresetPositions(normalized, storyOverviewLayoutTokens(entries.length).minimumDistance, 240)
}

export function relaxPresetPositions(
  positions: Readonly<Record<string, StoryOverviewPosition>>,
  minimumDistance: number,
  iterations = 180,
) {
  const ids = Object.keys(positions).sort()
  const points = Object.fromEntries(ids.map((id) => [id, { ...positions[id] }]))
  for (let iteration = 0; iteration < iterations; iteration += 1) {
    const movement = Object.fromEntries(ids.map((id) => [id, { x: 0, y: 0 }]))
    for (let leftIndex = 0; leftIndex < ids.length; leftIndex += 1) {
      for (let rightIndex = leftIndex + 1; rightIndex < ids.length; rightIndex += 1) {
        const leftId = ids[leftIndex]
        const rightId = ids[rightIndex]
        const left = points[leftId]
        const right = points[rightId]
        let dx = right.x - left.x
        let dy = right.y - left.y
        let distance = Math.hypot(dx, dy)
        if (distance >= minimumDistance) continue
        if (distance < 0.001) {
          const angle = (((leftIndex + 1) * 37 + (rightIndex + 1) * 71) % 360) * Math.PI / 180
          dx = Math.cos(angle)
          dy = Math.sin(angle)
          distance = 1
        }
        const push = (minimumDistance - distance) * 0.52
        const unitX = dx / distance
        const unitY = dy / distance
        movement[leftId].x -= unitX * push
        movement[leftId].y -= unitY * push
        movement[rightId].x += unitX * push
        movement[rightId].y += unitY * push
      }
    }
    ids.forEach((id) => {
      points[id].x += movement[id].x * 0.7
      points[id].y += movement[id].y * 0.7
    })
  }
  return points
}
